import { Button, Callout } from "@/components/ui";
import { Separator } from "@/components/ui/Separator";
import { BodyText } from "@/components/ui/Text";
import { QueryKeys } from "@/constants/Query";
import { ErrorBoundary } from "@/lib/monitoring";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef } from "react";
import { DEFAULT_CATEGORY, useFilesContext } from "../FilesContext";
import { useFile } from "../network/useFile";
import { useFileCategories } from "../network/useFileCategories";
import { EditFile } from "./EditFile";
import { EditFileSkeleton } from "./EditFileSkeleton";

export const FileDetail = () => {
  const { patientId, fileId } = useParams({
    from: "/patient/$patientId/files/$fileId",
  });
  const navigate = useNavigate();

  const { filteredFiles, isLoadingFiles, selectedCategory } = useFilesContext();
  const { data: categoriesData } = useFileCategories();
  const queryClient = useQueryClient();
  const hasInvalidatedFilesRef = useRef(false);
  const previousFileIdRef = useRef(fileId);
  const currentCategoryName =
    selectedCategory === DEFAULT_CATEGORY
      ? "All Files"
      : categoriesData?.categories.find((c) => c.id === selectedCategory)?.type;

  const currentFileIndex =
    filteredFiles?.findIndex((file) => file.id === fileId) ?? -1;
  const fileFromContext = filteredFiles?.[currentFileIndex];
  const isInContext = currentFileIndex >= 0;
  const hasPrev = currentFileIndex > 0;
  const hasNext =
    isInContext && currentFileIndex < (filteredFiles?.length ?? 0) - 1;

  // If the file isn't in the paginated context (e.g. opened directly from a
  // Chat link to a file older than the first page), fetch it by ID.
  const { data: fileFromDirectFetch, isLoading: isLoadingDirectFetch } =
    useFile({
      documentReferenceId: fileId,
      enabled: !isLoadingFiles && !isInContext,
    });

  const handleBack = () => {
    navigate({ to: "/patient/$patientId/files", params: { patientId } });
  };

  const handleNext = () => {
    if (currentFileIndex < (filteredFiles?.length ?? 0) - 1) {
      const nextFile = filteredFiles?.[currentFileIndex + 1];
      if (nextFile?.id) {
        navigate({
          to: "/patient/$patientId/files/$fileId",
          params: { patientId, fileId: nextFile.id },
        });
      }
    }
  };

  const handlePrev = () => {
    if (currentFileIndex > 0) {
      const prevFile = filteredFiles?.[currentFileIndex - 1];
      if (prevFile?.id) {
        navigate({
          to: "/patient/$patientId/files/$fileId",
          params: { patientId, fileId: prevFile.id },
        });
      }
    }
  };

  const handleSave = () => {
    navigate({ to: "/patient/$patientId/files", params: { patientId } });
  };

  const currentFile = fileFromContext ?? fileFromDirectFetch;
  const isLoading = isLoadingFiles || isLoadingDirectFetch;

  // When routed here from Chat for a file not in the cached list (e.g. a
  // real-time image the patient just uploaded), invalidate the list once so
  // navigating back to All Files reflects the latest data.
  useEffect(() => {
    if (previousFileIdRef.current !== fileId) {
      hasInvalidatedFilesRef.current = false;
      previousFileIdRef.current = fileId;
    }
  }, [fileId]);

  useEffect(() => {
    if (!isLoadingFiles && !isInContext && !hasInvalidatedFilesRef.current) {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.Patient.MedicalRecords.Files.DEFAULT(patientId),
      });
      hasInvalidatedFilesRef.current = true;
    }
  }, [isInContext, isLoadingFiles, patientId, queryClient]);

  return (
    <>
      <div className="flex justify-between items-center w-full mt-1">
        <Button
          variant="link"
          onClick={handleBack}
          leftIcon={ArrowLeft}
          className="font-normal"
        >
          All Files
        </Button>
        {isInContext && (
          <BodyText className="flex" size="sm">
            {currentCategoryName} ({currentFileIndex + 1} of{" "}
            {filteredFiles?.length})
          </BodyText>
        )}
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="link"
            onClick={handlePrev}
            leftIcon={ChevronLeft}
            className="font-normal"
            disabled={!hasPrev}
          >
            Prev
          </Button>

          <Button
            variant="link"
            onClick={handleNext}
            className="font-normal"
            disabled={!hasNext}
          >
            <span className="flex flex-row gap-1 items-start leading-[1.1rem]">
              Next
              <ChevronRight size={16} />
            </span>
          </Button>
        </div>
      </div>
      <Separator className="mt-2.5" />
      {/* In rare cases, PDF.js will throw unhandled errors that are not caught by react-pdf's error handling for some reason. This error boundary ensures it doesn't blow up the whole app. Usually
      they are resolved by leaving the FileDetail page and returning to it. */}
      {currentFile && !isLoading && (
        <ErrorBoundary
          fallback={
            <Callout
              variant="danger"
              title="An error has occurred loading the file"
              description="Please click the back button to go back to the files page and try again. If the problem persists, please log a bug to #care-blocking-bugs or #bugs."
            />
          }
        >
          <EditFile onSave={handleSave} file={currentFile} />
        </ErrorBoundary>
      )}
      {isLoading && <EditFileSkeleton />}
      {!currentFile && !isLoading && (
        <Callout
          className="mt-3"
          variant="warning"
          title="File not found"
          description="If this was loaded from the Chat, it may have been voided."
        />
      )}
    </>
  );
};
